
import { Modal, Radio } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { statusOptions } from '../../../../constants';
import { formatPascalCase, getEsimLicenses } from '../../../../utils';
import { useMergeState } from '../../../../utils/hooks';
import { toastrSuccess } from '../../../../utils/toastNotification';
import NormalButton from '../../buttons/normalButton';
import NormalInput from '../../inputs/normalInput';
import NormalSelect from '../../selects/normalSelect';
import { getForm, getSchema, updateCard } from './helper';

import './style.scss';

const EditCardInformationModal = (props) => {
  const [state, setState] = useMergeState({
    loading: false,
    ladingLicense: false,
    esimLicenses: [],
    editType: 1,
  });
  const initData = useRef({
  });

  const statusOption = statusOptions;

  const schema = getSchema();
  const form = getForm(schema);

  const [status, licenseName, brand, model] = form.watch(['status', 'licenseName', 'compatibleBrand', 'compatibleModel']);

  const onChangeRadio = (e) => {
    form.setValue('licenseName', initData.current.licenseName);
    form.setValue('compatibleBrand', initData.current.compatibleBrand);
    form.setValue('compatibleModel', initData.current.compatibleModel);
    setState({ editType: e.target.value });
  };

  const onCancel = () => {
    props.onCancel();
  };

  const onConfirm = (newCardInfo, changeLogs) => {
    props.onConfirm(newCardInfo, changeLogs);
    onCancel();
  };

  const checkValidForm = () => {
    if (state.editType === 2 && !brand?.trim()) return true;
    if (state.editType === 3 && !model?.trim()) return true;
    return false;
  };

  const checkIsChangedField = (changeLogs) => {
    if (changeLogs && changeLogs.length !== 0) {
      return '';
    }
    if (initData.current.status.value === form.getValues('status')?.value) {
      return 'Status';
    }
    if (state.editType === 1 && initData.current.licenseName.value === form.getValues('licenseName')?.value) {
      return 'License';
    }
    if (state.editType === 2 && initData.current.compatibleBrand === form.getValues('compatibleBrand')) {
      return 'Brand';
    }
    if (state.editType === 3 && initData.current.compatibleModel === form.getValues('compatibleModel')) {
      return 'Model';
    }
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const changeLogs = [];
    Object.keys(initData.current).forEach((key) => {
      switch (key) {
        case 'eID':
          return;
        case 'status':
          if (initData.current[key].value !== form.getValues(key).value) {
            const newChangeLog = {
              dateTime: moment(),
              column: key,
              valueBefore: initData.current[key].value,
              valueAfter: form.getValues(key).value,
            };
            changeLogs.push(newChangeLog);
          }
          return;
        case 'licenseName':
          if (!_.isEqual(initData.current[key], form.getValues(key))) {
            const oldLic = initData.current[key];
            const newLic = form.getValues(key);
            const newChangeLog = {
              dateTime: moment(),
              column: 'license_id',
            };
            Object.assign(newChangeLog, {
              licenseBefore: {
                name: oldLic?.label,
                id: oldLic?.value,
              },
              licenseAfter: {
                name: newLic?.label,
                id: newLic?.value,
              },
            });
            changeLogs.push(newChangeLog);
          }
          return;
        case 'compatibleBrand':
          if (initData.current[key] !== form.getValues(key)) {
            const newChangeLog = {
              dateTime: moment(),
              column: 'compatible_brand',
              valueBefore: initData.current[key],
              valueAfter: form.getValues(key),
            };
            changeLogs.push(newChangeLog);
          }
          return;
        case 'compatibleModel':
          if (initData.current[key] !== form.getValues(key)) {
            const newChangeLog = {
              dateTime: moment(),
              column: 'compatible_model',
              valueBefore: initData.current[key],
              valueAfter: form.getValues(key),
            };
            changeLogs.push(newChangeLog);
          }
          return;
        default:
      }
    });
    const checkChanges = (checkIsChangedField(changeLogs));
    if (checkChanges) {
      Modal.warning({
        title: `The new ${checkChanges} is the same as the current one`,
      });
      return;
    }
    setState({ loading: true });
    const updatedCard = await updateCard(changeLogs, form.getValues('eID'));
    if (updatedCard?.isSuccess) {
      toastrSuccess("Card's information changed successfully");
      onConfirm(updatedCard.esim, changeLogs);
    }
    setState({ loading: false });
  };

  const initMasterData = async () => {
    setState({ loadingLicense: true });
    const esimLicenses = await getEsimLicenses();
    if (esimLicenses.isSuccess) {
      const esimLicenseOptions = esimLicenses.licenses.map(license => ({
        label: license.name,
        value: license.id,
        data: {
          isAllowedDual: license.isAllowedDual,
          licenseType: license.licenseType,
          storage: license.storage,
        },
      }));
      setState({ esimLicenses: esimLicenseOptions });
    }
    setState({ loadingLicense: false });
  };

  useEffect(() => {
    if (props.visible) {
      if (_.isEmpty(state.esimLicenses)) { initMasterData(); }
      initData.current = {
        eID: props.currentInfo.eId[0].value,
        status: { value: props.currentInfo.eId[1].value === 'Blocked', label: props.currentInfo.eId[1].value },
        compatibleBrand: props.currentInfo.eId[5].value === '--' ? '' : props.currentInfo.eId[5].value,
        compatibleModel: props.currentInfo.eId[6].value === '--' ? '' : props.currentInfo.eId[6].value,
        licenseName: props.currentInfo?.currentLicense ? {
          value: props.currentInfo?.currentLicense?.id,
          label: props.currentInfo?.currentLicense?.name,
          data: {
            storage: props.currentInfo?.currentLicense?.storage,
            licenseType: props.currentInfo?.currentLicense?.licenseType || undefined,
            isAllowedDual: props.currentInfo?.currentLicense?.isAllowedDual,
          },
        } : undefined,
      };
      form.reset(initData.current);
      setState({ editType: 1 });
    }
    // form.reset({}, { keepDefaultValues: true });
  }, [props.visible]);
  return (
    <Modal
      className="edit-card-info-modal"
      title="Edit card's information"
      visible={props.visible}
      onCancel={props.onCancel}
      maskClosable={props.maskClosable}
      footer={[
        <NormalButton
          key="save-changes"
          text="Save changes"
          type="primary"
          loading={state.loading}
          disabled={checkValidForm()}
          onClick={onSubmit}
        />,
      ]}
    >
      <form onSubmit={onSubmit}>
        <NormalInput
          name="eID"
          title="eID"
          isUseForm
          control={form?.control}
          disabled
        />
        <NormalSelect
          className="mt-10"
          name="status"
          title="Status"
          isRequired
          isUseForm
          control={form?.control}
          options={statusOption}
          placeholder="Status"
          isObject
        />
        <Radio
          className="mt-12"
          value={1}
          onChange={onChangeRadio}
          checked={state.editType === 1}
          disabled={status?.value}
        >
          Edit license

        </Radio>
        <NormalSelect
          className="ml-24 mt-10"
          title="License name"
          name="licenseName"
          isRequired
          isUseForm
          control={form?.control}
          options={state.esimLicenses}
          loading={state.loadingLicense}
          placeholder="License name"
          isObject
          disabled={state.editType !== 1 || status?.value}
        />
        <NormalInput
          className="ml-24 mt-10"
          title="eStorage"
          name="eStorage"
          placeholder="eStorage"
          value={licenseName?.data.storage}
          disabled
        />
        <NormalInput
          className="ml-24 mt-10"
          title="License type"
          name="licenseType"
          placeholder="License type"
          value={licenseName?.data?.licenseType ? formatPascalCase(licenseName?.data?.licenseType) : undefined}
          disabled
        />
        <NormalInput
          className="ml-24 mt-10"
          title="Dual-eSIM"
          name="dualEsim"
          placeholder="Dual-eSIM"
          value={licenseName ? licenseName?.data?.isAllowedDual ? 'Allowed' : 'Not allowed' : undefined}
          disabled
        />
        <Radio
          className="mt-12"
          value={2}
          onChange={onChangeRadio}
          checked={state.editType === 2}
          disabled={status?.value}
        >
          Edit brand

        </Radio>
        <NormalInput
          className="mt-10 ml-24"
          title="Brand"
          placeholder="Brand"
          name="compatibleBrand"
          value={licenseName?.data.storage}
          disabled={state.editType !== 2 || status?.value}
          isRequired
          isUseForm
          control={form?.control}
        />
        <Radio
          className="mt-12"
          value={3}
          onChange={onChangeRadio}
          checked={state.editType === 3}
          disabled={status?.value}
        >
          Edit model

        </Radio>
        <NormalInput
          className="mt-10 ml-24"
          title="Model"
          placeholder="Model"
          name="compatibleModel"
          isRequired
          disabled={state.editType !== 3 || status?.value}
          isUseForm
          control={form?.control}
        />
      </form>
    </Modal>
  );
};

EditCardInformationModal.defaultProps = {
  visible: false,
  maskClosable: false,
  currentInfo: {},

  onCancel: () => {},
  onConfirm: () => {},
};

EditCardInformationModal.propTypes = {
  visible: PropTypes.bool,
  maskClosable: PropTypes.bool,
  currentInfo: PropTypes.shape(),

  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default EditCardInformationModal;
